import React from 'react';
import clsx from 'clsx';
import type { TableColumn, ColumnFilters, ColumnFilterValue } from '@app-types';
import ColumnFiltersRow from '@components/common/table/ColumnFiltersRow';
import styles from './TableHeader.module.css';

interface TableHeaderProps {
  columns: TableColumn[];
  onColumnSort: (columnKey: string) => void;
  getSortIndicator: (columnKey: string) => string | null;
  isColumnSorted: (columnKey: string) => boolean;
  /** Optional column filters configuration */
  columnFilters?: {
    values: ColumnFilters;
    onChange: (columnKey: string, value: ColumnFilterValue | null) => void;
    onClearAll: () => void;
    hasActiveFilters: boolean;
  };
}

/**
 * Generic table header component with sorting and column filtering support
 * Used across all schedule-related tables (Scheduler, Assessor)
 */
const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  onColumnSort,
  getSortIndicator,
  isColumnSorted,
  columnFilters
}) => {
  // Add extra column for sticky Clear button when filters are active
  const hasActiveClearButton = columnFilters?.hasActiveFilters;

  return (
    <thead>
      <tr className={styles.tableHeader}>
        {columns.map((column) => {
          const isSortable = column.sortable !== false;

          return (
            <th
              key={column.key}
              className={clsx(isSortable && styles.sortableHeader, isSortable && isColumnSorted(column.key) && styles.sorted)}
              onClick={isSortable ? () => onColumnSort(column.key) : undefined}
              title={isSortable ? `Click to sort by ${column.label}` : undefined}
            >
              <div className={clsx(styles.headerContent, column.centered && styles.centered)}>
                <span className={styles.headerLabel}>{column.label}</span>
                {isSortable && <span className={styles.sortIndicator}>{getSortIndicator(column.key)}</span>}
              </div>
            </th>
          );
        })}
        {/* Empty header cell to align with sticky Clear button */}
        {hasActiveClearButton && <th className="table-header-clear-spacer" />}
      </tr>
      {/* Column filters row */}
      {columnFilters && (
        <ColumnFiltersRow
          columns={columns}
          filterValues={columnFilters.values}
          onFilterChange={columnFilters.onChange}
          onClearAll={columnFilters.onClearAll}
          hasActiveFilters={columnFilters.hasActiveFilters}
        />
      )}
    </thead>
  );
};

export default TableHeader;
